import type { FocusEvent } from 'react';
import type {
  ChangeHandler,
  FieldValues,
  Path,
  PathValue,
  UseFormRegister,
  UseFormRegisterReturn,
  UseFormSetValue,
} from 'react-hook-form';

/**
 * OM-4602 — padding typed into the wizard's free-form boxes must never be stored.
 *
 * The first pass at this trimmed inside `buildOnboardingPayload`, the one boundary where the
 * whole submit is assembled. QA rejected it, and the reason is that a trim nobody can see fixes
 * only half the bug: the padded value still sat in form state, so the anchored rules kept
 * failing on it — a trailing space after an address made Email read "Invalid format" rather
 * than being cleaned up, and the same for Zip, Salesforce ID, NMLS ID and the ML logins. What
 * the operator saw was never what would be stored.
 *
 * So the trim moved to the input boundary. On blur, not on change: trimming as the operator
 * types would eat the space between two words before the second one is typed. The submit-time
 * trim stays on as a backstop for any value that reaches the payload without passing through a
 * field.
 */

/** The shared half: commit a trimmed value, but only when trimming actually changed it. */
const commitTrimmed = (target: unknown, commit: (value: string) => void): void => {
  const value = (target as { value?: unknown } | null)?.value;
  if (typeof value !== 'string') return;
  const trimmed = value.trim();
  if (trimmed !== value) commit(trimmed);
};

/** Trims a controlled input's value when it loses focus. */
export const trimOnBlur =
  (commit: (value: string) => void) =>
  (event: FocusEvent<HTMLInputElement>): void =>
    commitTrimmed(event.target, commit);

/**
 * The same thing for a react-hook-form field: `registerTrimmed(register, setValue)('city')`
 * stands in for `register('city')`.
 *
 * `setValue` runs before the form's own blur handler so that handler reads the trimmed value —
 * registering a field hands RHF the input's ref, and setValue writes through it synchronously.
 * `shouldValidate` is what clears an error the padding alone was causing.
 */
export function registerTrimmed<T extends FieldValues>(
  register: UseFormRegister<T>,
  setValue: UseFormSetValue<T>,
) {
  return (name: Path<T>): UseFormRegisterReturn<Path<T>> => {
    const field = register(name);
    const onBlur: ChangeHandler = (event) => {
      commitTrimmed(event.target, (value) =>
        setValue(name, value as PathValue<T, Path<T>>, {
          shouldValidate: true,
          shouldDirty: true,
        }),
      );
      return field.onBlur(event);
    };
    return { ...field, onBlur };
  };
}
